import React, { MouseEventHandler, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiChevronDown, BiSolidBot, BiSolidChat, BiSolidMessageDetail } from 'react-icons/bi';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { useGetAllScenariosQuery } from '../../app/services/chatbot-rtk.services';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { BiLouise } from '../appearance/AppearanceSharedAi';
import { handleSetCloneExtra, wizardState } from './botSlice';
import CloneExternalLinks from './CloneExternalLinks';
import { AIKittyType } from './types';

type Props = {
  formBuilder: UseFormReturn<any, any>;
};
const CloneExtraVideoEdit = ({ formBuilder }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = formBuilder;

  const types: { [key: string]: string } = {
    MCBOT_01: t('DashboardCreatePartial/key3'),
    VIDEO_SCENARIO: t('DashboardCreatePartial/key5'),
  };

  const wizard = useAppSelector(wizardState);

  const { isLoading, isError, isSuccess, refetch, data } = useGetAllScenariosQuery({ botType: 'widget' });

  const handleUpdateExternalLinks = (
    externalLinks: Array<{
      logo: string;
      link: string;
    }>
  ) => {
    setValue('externalLinks', externalLinks, { shouldDirty: true });

    handleUpdate();
  };

  const renderVideoFloatingButton = (icon: { type: string; value: string }) => {
    const icons: Record<string, React.ReactNode> = {
      '4': <BiLouise />,
      '1': <BiSolidBot size={30} />,
      '2': <BiSolidChat size={30} />,
      '3': <BiSolidMessageDetail size={30} />,
    };

    if (icon.type === 'icon')
      return (
        <div className="tw-w-6 tw-h-6 tw-mr-2 tw-rounded-full tw-object-cover tw-border tw-border-solid tw-border-gray-300 tw-shrink-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-blue-500 tw-text-white">
          {icons[icon.value]}
        </div>
      );
    if (icon.type === 'image')
      return (
        <img
          src={icon.value}
          alt={''}
          className="tw-w-6 tw-h-6 tw-mr-2 tw-rounded-full tw-object-cover tw-border tw-border-solid tw-border-gray-300 tw-shrink-0"
        />
      );
    if (icon.type === 'video')
      return (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="tw-w-6 tw-h-6 tw-mr-2 tw-rounded-full tw-object-cover tw-border tw-border-solid tw-border-gray-300 tw-shrink-0"
        >
          <source src={icon.value}></source>
        </video>
      );

    return null;
  };

  const handleUpdate = () => {
    dispatch(
      handleSetCloneExtra({
        videoBotId: watch('videoBotId'),
        externalLinks: watch('externalLinks') ?? [],
      })
    );
  };

  useEffect(() => {
    if ((wizard.bot_config as AIKittyType).cloneExtra?.externalLinks)
      setValue('externalLinks', (wizard.bot_config as AIKittyType).cloneExtra?.externalLinks, { shouldDirty: true });
  }, []);

  const CustomToggle = React.forwardRef(
    ({ children, onClick }: { children: React.ReactNode; onClick: MouseEventHandler }, ref: React.LegacyRef<HTMLButtonElement>) => (
      <button
        id="btn-clone-video-edit-custom-toggle"
        className="tw-border-none tw-outline-none tw-ring-0 tw-bg-transparent tw-p-0 tw-m-0 tw- tw-h-[58px] tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex"
        ref={ref}
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        type="button"
      >
        <div className="tw-self-stretch tw-h-[58px] tw-flex-col tw-justify-start tw-items-start tw-flex">
          <div className="tw-self-stretch tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex">
            <div className="tw-grow tw-shrink tw-basis-0 tw-h-[58px] tw-pl-[17px] tw-pr-3 tw-py-[9px] tw-justify-start tw-items-center tw-gap-2 tw-flex">
              <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex">{children}</div>
            </div>
            <div className="tw-px-[13px] tw-py-[9px] tw-bg-white tw-justify-start tw-items-center tw-gap-1 tw-flex">
              <BiChevronDown size={24} />
            </div>
          </div>
        </div>
      </button>
    )
  );
  CustomToggle.displayName = 'CustomToggle';

  return (
    <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex  tw-w-full">
      <div className="tw-flex tw-flex-col tw-gap-y-6">
        <div className="description-infos">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Wizard/CloneExtraVideo/key1')}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">{t('Wizard/CloneExtraVideo/key2')}</div>
            </div>
          </div>

          <Controller
            control={control}
            name="videoBotId"
            defaultValue={(wizard.bot_config as AIKittyType).cloneExtra?.data?.botId ?? ''}
            render={({ field }) => (
              <React.Fragment>
                {isError ? (
                  <PSButton id="btn-clone-video-refetch" type="button" onClick={refetch} title="Retry" />
                ) : (
                  <Dropdown>
                    <Dropdown.Toggle as={CustomToggle}>
                      {field.value ? (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-relative">
                          {data && data?.find(item => item.id === field.value)
                            ? renderVideoFloatingButton(data.find(item => item.id === field.value)?.icon as any)
                            : null}
                          <div className="tw-text-left">
                            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                              {data?.find(item => item.id === field.value)?.title}
                            </div>
                            <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                              {types[data?.find(item => item.id === field.value)?.type as string]}
                            </div>
                            <div
                              className={`tw-w-4 tw-h-4 tw-absolute tw-left-4 tw-bottom-0 ${
                                !data?.find(item => item.id === field.value)?.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                              } tw-rounded-full tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                            />
                          </div>
                        </div>
                      ) : (
                        t('Wizard/CloneExtraVideo/key3')
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="tw-w-full">
                      {isLoading ? (
                        <Dropdown.Item>Loading...</Dropdown.Item>
                      ) : isSuccess &&
                        data?.filter(item => item.type === BotTypeEnum.VIDEO_SCENARIO || item.type === BotTypeEnum.TEXT_SCENARIO)?.length === 0 ? (
                        <Dropdown.Item>{t('Wizard/CloneExtraVideo/key4')}</Dropdown.Item>
                      ) : (
                        <React.Fragment>
                          {data
                            ?.filter(item => item.type === BotTypeEnum.VIDEO_SCENARIO || item.type === BotTypeEnum.TEXT_SCENARIO)
                            ?.map((opt, index) => (
                              <Dropdown.Item
                                key={index}
                                onClick={() => {
                                  setValue('videoBotId', opt.id, {
                                    shouldDirty: true,
                                  });

                                  clearErrors('videoBotId');

                                  handleUpdate();
                                }}
                              >
                                <div className="tw-flex tw-items-center tw-gap-2 tw-relative">
                                  {renderVideoFloatingButton(opt.icon)}
                                  <div className="tw-text-left">
                                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{opt.title}</div>
                                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{types[opt.type]}</div>
                                    <div
                                      className={`tw-w-4 tw-h-4 tw-absolute tw-left-4 tw-bottom-0 ${
                                        !opt?.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                                      } tw-rounded-full tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                                    />
                                  </div>
                                </div>
                              </Dropdown.Item>
                            ))}
                        </React.Fragment>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                {!!errors.videoBotId && <span className="tw-text-sm tw-text-red-600 tw-mt-2 tw-block">{errors.videoBotId?.message as string}</span>}
              </React.Fragment>
            )}
          />
        </div>

        <div className="external-links">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Wizard/CloneExtraDescription/key9')}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('Wizard/CloneExtraDescription/key10')}
              </div>
            </div>
          </div>

          <CloneExternalLinks initialData={(wizard.bot_config as AIKittyType).cloneExtra?.externalLinks ?? []} onChange={handleUpdateExternalLinks} />

          {!!errors.externalLinks && <span className="tw-text-sm tw-text-red-600 tw-mt-2 tw-block">{errors.externalLinks?.message as string}</span>}
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(CloneExtraVideoEdit);
